import React, { useState } from 'react';
import { X } from 'lucide-react';
import { equipmentAPI } from '../../services/api';

export default function AddFuelLog({ equipmentId, fuelType, existingLog, onClose, onSaved }) {
  const isEdit = !!existingLog;
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    log_date: existingLog?.log_date
      ? new Date(existingLog.log_date).toISOString().slice(0, 10)
      : new Date().toISOString().slice(0, 10),
    fuel_type: existingLog?.fuel_type || fuelType || 'Diesel',
    quantity_litres: existingLog?.quantity_litres ?? '',
    cost_per_litre: existingLog?.cost_per_litre ?? '',
    total_cost: existingLog?.total_cost ?? '',
    odometer_reading: existingLog?.odometer_reading ?? '',
    filled_by: existingLog?.filled_by || '',
    vendor: existingLog?.vendor || '',
    notes: existingLog?.notes || '',
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => {
      const next = { ...f, [name]: value };
      if ((name === 'quantity_litres' || name === 'cost_per_litre') && !isEdit) {
        const qty = parseFloat(name === 'quantity_litres' ? value : next.quantity_litres) || 0;
        const rate = parseFloat(name === 'cost_per_litre' ? value : next.cost_per_litre) || 0;
        if (qty > 0 && rate > 0) next.total_cost = (qty * rate).toFixed(2);
      }
      return next;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.quantity_litres || parseFloat(form.quantity_litres) <= 0) {
      alert('Quantity is required'); return;
    }
    setLoading(true);
    try {
      const payload = { ...form };
      payload.quantity_litres = parseFloat(payload.quantity_litres);
      if (payload.cost_per_litre) payload.cost_per_litre = parseFloat(payload.cost_per_litre);
      if (payload.total_cost) payload.total_cost = parseFloat(payload.total_cost);
      if (payload.odometer_reading) payload.odometer_reading = parseFloat(payload.odometer_reading);

      if (isEdit) {
        await equipmentAPI.updateFuelLog(equipmentId, existingLog.id, payload);
      } else {
        await equipmentAPI.addFuelLog(equipmentId, payload);
      }
      onSaved();
    } catch (err) {
      alert(err.message || `Failed to ${isEdit ? 'update' : 'add'} fuel log`);
    } finally {
      setLoading(false);
    }
  };

  const inputCls = 'w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-500';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-lg font-semibold">{isEdit ? 'Edit Fuel Log' : 'Add Fuel Log'}</h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <X className="h-5 w-5" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Date *</label>
              <input type="date" name="log_date" value={form.log_date} onChange={handleChange} required className={inputCls} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Fuel Type</label>
              <select name="fuel_type" value={form.fuel_type} onChange={handleChange} className={inputCls}>
                <option value="Diesel">Diesel</option>
                <option value="Petrol">Petrol</option>
              </select>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Quantity (Litres) *</label>
            <input type="number" step="0.01" min="0" name="quantity_litres" value={form.quantity_litres} onChange={handleChange} required className={inputCls} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Cost per Litre (₹)</label>
              <input type="number" step="0.01" min="0" name="cost_per_litre" value={form.cost_per_litre} onChange={handleChange} className={inputCls} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Total Cost (₹)</label>
              <input type="number" step="0.01" min="0" name="total_cost" value={form.total_cost} onChange={handleChange} className={inputCls} />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Odometer / Hour Meter</label>
            <input type="number" step="0.01" name="odometer_reading" value={form.odometer_reading} onChange={handleChange} className={inputCls} placeholder="Current reading" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Filled By</label>
              <input name="filled_by" value={form.filled_by} onChange={handleChange} className={inputCls} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Vendor / Station</label>
              <input name="vendor" value={form.vendor} onChange={handleChange} className={inputCls} />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
            <textarea name="notes" value={form.notes} onChange={handleChange} rows={2} className={inputCls} />
          </div>
          <div className="flex gap-3 pt-4">
            <button type="submit" disabled={loading} className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50">
              {loading ? 'Saving...' : isEdit ? 'Update' : 'Save'}
            </button>
            <button type="button" onClick={onClose} className="px-4 py-2 border border-gray-200 rounded-lg hover:bg-gray-50">
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
